import type { ReactNode } from "react";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import { EmptyBlock, SectionCard } from "../../components/primitives";
import { formatCount, formatMs, formatPercent } from "../../format";
import type { HistSummary, MetricSeries } from "../../types";
import { SeriesSwatch } from "../backend/stat-tile";
import { failureTone, LIMITS, toneText, type Tone } from "../backend/tone";
import { podColor } from "./pod-order";

interface TotalsRow {
  key: string;
  endpoint: string | null;
  index: number;
  attempts: number;
  failed: number;
  timeouts: number;
  latency: HistSummary | undefined;
  ping: HistSummary | undefined;
  inPool: boolean;
}

const rateText = (failed: number, attempts: number) => {
  if (!attempts) return "–";
  const rate = failed / attempts;
  return formatPercent(rate, rate > 0 && rate < 0.1 ? 1 : 0);
};

const p95Tone = (h: HistSummary | undefined): Tone => ((h?.p95 ?? 0) > LIMITS.podSlowMs ? "warn" : "neutral");

/** Window totals per pod: attempts, failures, latency of single attempts, ping (table on md+, cards below). */
export function PodTotals({ metrics, order, live }: { metrics: MetricSeries; order: string[]; live: Set<string> }) {
  const { t } = useLocalization();
  const counters = metrics.totals?.counters ?? {};
  const hists = metrics.totals?.hists ?? {};
  const c = (name: string) => counters[name] ?? 0;

  const rows: TotalsRow[] = order
    .map((endpoint, index) => {
      const ok = c(`pod.ok@${endpoint}`);
      const fail = c(`pod.fail@${endpoint}`);
      const timeouts = c(`pod.timeout@${endpoint}`);
      return {
        key: endpoint,
        endpoint,
        index,
        attempts: ok + fail + timeouts,
        failed: fail + timeouts,
        timeouts,
        latency: hists[`pod.request_ms@${endpoint}`],
        ping: hists[`pod.ping_ms@${endpoint}`],
        inPool: live.has(endpoint),
      };
    })
    .filter((row) => row.attempts > 0 || row.inPool || (row.ping?.n ?? 0) > 0);

  const all: TotalsRow = {
    key: "__all",
    endpoint: null,
    index: -1,
    attempts: c("pod.ok") + c("pod.fail") + c("pod.timeout"),
    failed: c("pod.fail") + c("pod.timeout"),
    timeouts: c("pod.timeout"),
    latency: hists["pod.request_ms"],
    ping: undefined,
    inPool: true,
  };

  const windowLabel = t(`monitoring.window.long.${metrics.window}` as TranslationKey);

  return (
    <SectionCard
      title={t("monitoring.pods.totals.title")}
      description={t("monitoring.pods.totals.description", { window: windowLabel })}
    >
      {rows.length === 0 && all.attempts === 0 ? (
        <EmptyBlock>{t("monitoring.pods.totals.empty")}</EmptyBlock>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="hidden md:block">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-8">{t("monitoring.pods.col.pod")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.attempts")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.failures")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.failureRate")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.p50")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.p95")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.max")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.pods.col.ping")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TotalsTableRow key={row.key} row={row} />
                ))}
              </TableBody>
              <TableFooter className="bg-transparent">
                <TotalsTableRow row={all} />
              </TableFooter>
            </Table>
          </div>
          <div className="flex flex-col gap-2 md:hidden">
            {rows.map((row) => (
              <TotalsCard key={row.key} row={row} />
            ))}
            <TotalsCard row={all} />
          </div>
          <p className="text-[11px] text-muted-foreground">{t("monitoring.pods.totals.note")}</p>
        </div>
      )}
    </SectionCard>
  );
}

function PodLabel({ row }: { row: TotalsRow }) {
  const { t } = useLocalization();
  if (!row.endpoint) return <span className="font-medium">{t("monitoring.pods.totals.all")}</span>;
  return (
    <span className="inline-flex min-w-0 items-start gap-2">
      <SeriesSwatch color={podColor(row.index)} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block break-all font-mono">{row.endpoint}</span>
        {!row.inPool && <span className="block text-[11px] text-muted-foreground">{t("monitoring.pods.totals.notInPool")}</span>}
      </span>
    </span>
  );
}

function FailedCell({ row }: { row: TotalsRow }) {
  const { t } = useLocalization();
  return (
    <span className={cn(row.failed > 0 && toneText(failureTone(row.failed, row.attempts)))}>
      {formatCount(row.failed)}
      {row.timeouts > 0 && (
        <span className="text-muted-foreground"> {t("monitoring.pods.totals.timeouts", { count: formatCount(row.timeouts) })}</span>
      )}
    </span>
  );
}

function TotalsTableRow({ row }: { row: TotalsRow }) {
  const rateTone = failureTone(row.failed, row.attempts);
  return (
    <TableRow className={cn(!row.endpoint && "hover:bg-transparent")}>
      <TableCell className="max-w-[16rem] py-2">
        <PodLabel row={row} />
      </TableCell>
      <TableCell className="py-2 text-right font-mono tabular-nums">{formatCount(row.attempts)}</TableCell>
      <TableCell className="py-2 text-right font-mono tabular-nums">
        <FailedCell row={row} />
      </TableCell>
      <TableCell className={cn("py-2 text-right font-mono tabular-nums", toneText(rateTone))}>
        {rateText(row.failed, row.attempts)}
      </TableCell>
      <TableCell className="py-2 text-right font-mono tabular-nums">{formatMs(row.latency?.p50)}</TableCell>
      <TableCell className={cn("py-2 text-right font-mono tabular-nums", toneText(p95Tone(row.latency)))}>
        {formatMs(row.latency?.p95)}
      </TableCell>
      <TableCell className="py-2 text-right font-mono tabular-nums">{formatMs(row.latency?.max)}</TableCell>
      <TableCell className="py-2 text-right font-mono tabular-nums">{formatMs(row.ping?.p95)}</TableCell>
    </TableRow>
  );
}

function TotalsCard({ row }: { row: TotalsRow }) {
  const { t } = useLocalization();
  const rateTone = failureTone(row.failed, row.attempts);
  const cell = (label: string, value: ReactNode, tone?: Tone) => (
    <div className="flex min-w-0 items-baseline justify-between gap-2 border-b border-border/50 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-mono tabular-nums", toneText(tone))}>{value}</span>
    </div>
  );
  return (
    <div className="rounded-md border px-3 py-2 text-xs">
      <div className="mb-1.5">
        <PodLabel row={row} />
      </div>
      <div className="grid grid-cols-1 gap-x-4 min-[360px]:grid-cols-2">
        {cell(t("monitoring.pods.col.attempts"), formatCount(row.attempts))}
        {cell(t("monitoring.pods.col.failures"), <FailedCell row={row} />)}
        {cell(t("monitoring.pods.col.failureRate"), rateText(row.failed, row.attempts), rateTone)}
        {cell(t("monitoring.pods.col.p50"), formatMs(row.latency?.p50))}
        {cell(t("monitoring.pods.col.p95"), formatMs(row.latency?.p95), p95Tone(row.latency))}
        {cell(t("monitoring.pods.col.max"), formatMs(row.latency?.max))}
        {row.endpoint && cell(t("monitoring.pods.col.ping"), formatMs(row.ping?.p95))}
      </div>
    </div>
  );
}
